/**
 * hm-sales-report.ts — zestawienie sprzedaży („Sprzedaż za okres") modułu „Handel i Magazyn".
 *
 * Symfonia WebAPI NIE MA żadnej trasy raportowej — oddaje wyłącznie listy dokumentów i wydruki
 * PDF pojedynczych dokumentów. Cały raport liczymy więc u siebie, z tej samej listy, którą
 * kartoteka i tak pobiera.
 *
 * Dlaczego funkcja licząca mieszka w pakiecie współdzielonym, a nie w API: wymiary wymagające
 * szczegółów dokumentu (rejestr pieniężny, towar, stawka VAT) front dociąga paczkami PO
 * wyrenderowaniu raportu i przelicza go u siebie. Obie strony muszą liczyć TYM SAMYM kodem —
 * inaczej suma po dociągnięciu potrafiłaby się różnić od tej sprzed dociągnięcia.
 *
 * Pieniądz: kwoty jako tekst dziesiętny (konwencja repo), sumowanie przez GROSZE na liczbach
 * całkowitych. Sumy NIGDY nie schodzą do jednej liczby — to zawsze lista per waluta, bo kursu
 * z listy Symfonii nie znamy i nie zgadujemy.
 */

import {
  bucketById,
  bucketByText,
  compareBuckets,
  compareCurrency,
  fromMinor,
  HM_REPORT_NONE,
  HM_REPORT_NONE_KEY,
  type HmReportBucketIdentity,
  toMinor,
} from './hm-report-core';

export {
  HM_REPORT_DETAILS_CHUNK,
  HM_REPORT_DETAILS_MAX,
  HM_REPORT_NONE_KEY,
} from './hm-report-core';

/** Wymiary podsumowania. `none` = raport płaski, bez koszyków. */
export const HM_SALES_REPORT_DIMENSIONS = [
  'none',
  'buyer',
  'recipient',
  'typeCode',
  'character',
  'series',
  'department',
  'description',
  'currency',
  'status',
  'issueDate',
  'issueMonth',
  'paymentRegistry',
  'product',
  'vatRate',
] as const;

export type HmSalesReportDimension = (typeof HM_SALES_REPORT_DIMENSIONS)[number];

/**
 * Wymiary, których Symfonia NIE podaje na liście — wymagają pobrania szczegółu KAŻDEGO
 * dokumentu osobno (prawdziwe N+1, brak pobrania hurtem). Dlatego są za jawnym przełącznikiem.
 */
export const HM_SALES_REPORT_DETAIL_DIMENSIONS = ['paymentRegistry', 'product', 'vatRate'] as const;

export function hmReportDimensionNeedsDetails(dimension: HmSalesReportDimension): boolean {
  return (HM_SALES_REPORT_DETAIL_DIMENSIONS as readonly string[]).includes(dimension);
}

/**
 * Wymiary liczone Z POZYCJI dokumentu — przełączają ziarno wiersza (jeden dokument = tyle
 * wierszy, ile pozycji). Rejestr pieniężny jest polem NAGŁÓWKA, więc go tu nie ma, mimo że
 * też wymaga szczegółu.
 */
export const HM_SALES_REPORT_POSITION_DIMENSIONS = ['product', 'vatRate'] as const;

export function hmReportDimensionNeedsPositions(dimension: HmSalesReportDimension): boolean {
  return (HM_SALES_REPORT_POSITION_DIMENSIONS as readonly string[]).includes(dimension);
}

/** Ziarno wiersza raportu. */
export type HmSalesReportGrain = 'document' | 'position';

export interface HmSalesReportRow {
  documentId: number;
  documentNumber: string;
  buyerId: number | null;
  buyerName: string | null;
  buyerIsAdHoc: boolean;
  recipientId: number | null;
  recipientName: string | null;
  issueDate: string | null;
  saleDate: string | null;
  typeCode: string;
  typeName: string | null;
  /** Charakter dokumentu — Symfonia oddaje go osobną trasą per charakter, my mamy go w słowniku typów. */
  character: string | null;
  series: string;
  departmentId: number | null;
  departmentName: string | null;
  description: string;
  /** Zawsze konkretna waluta — pusty string z Symfonii jest tłumaczony na `PLN`. */
  currency: string;
  inBuffer: boolean;
  /** Wypełnione dopiero po dociągnięciu szczegółów. */
  paymentRegistryId: number | null;
  paymentRegistryName: string | null;
  netValue: string;
  /**
   * VAT jest w ZŁOTÓWKACH nawet dla dokumentu walutowego (`VatValuePLN` w kontrakcie Symfonii),
   * podczas gdy netto i brutto są w walucie dokumentu. Dla faktury w euro „netto + VAT = brutto"
   * się NIE ZGADZA i to jest poprawne — dlatego kolumna nazywa się „VAT (PLN)".
   */
  vatPln: string;
  grossValue: string;
  /** Wypełnione WYŁĄCZNIE dla ziarna pozycji. */
  positionNo: number | null;
  productCode: string | null;
  productName: string | null;
  vatRateCode: string | null;
  quantity: string | null;
}

/** Kwoty jednej waluty. Suma raportu to ZAWSZE lista takich pozycji, nawet dla samych złotówek. */
export interface HmReportMoney {
  currency: string;
  net: string;
  vatPln: string;
  gross: string;
}

export interface HmSalesReportBucket {
  key: string;
  /** Etykieta wzięta z danych (nazwa kontrahenta, seria, data). `null` = brak wartości. */
  label: string | null;
  /**
   * Token do przetłumaczenia na froncie — dla wymiarów, których etykieta NIE jest daną
   * (dziś tylko status: `buffer` / `issued`). Ustawiony ⇒ wygrywa z `label`.
   */
  labelKey: string | null;
  count: number;
  documentCount: number;
  totals: HmReportMoney[];
  rows: HmSalesReportRow[];
}

export interface HmSalesReportAggregate {
  /** Wiersze płaskie — wypełnione tylko przy wymiarze `none`. */
  rows: HmSalesReportRow[];
  /** Koszyki — `null` przy wymiarze `none`. */
  buckets: HmSalesReportBucket[] | null;
  grandTotals: HmReportMoney[];
  /**
   * Suma z NAGŁÓWKÓW dokumentów — tylko przy ziarnie pozycji, do uzgodnienia. Symfonia potrafi
   * mieć na nagłówku narzuty i zaokrąglenia, których nie ma w pozycjach; pokazujemy rozbieżność
   * zamiast po cichu wybierać jedną liczbę.
   */
  documentTotals: HmReportMoney[] | null;
  /** `true` = suma z pozycji różni się od sumy z nagłówków o więcej niż grosz. */
  totalsMismatch: boolean;
  documentCount: number;
  positionCount: number | null;
}

/** Parametry raportu — to samo, co siedzi w adresie zakładki. */
export interface HmSalesReportParams {
  dateFrom: string | null;
  dateTo: string | null;
  buyerId: number | null;
  typeCode: string | null;
  departmentId: number | null;
  series: string | null;
  /** `null` = bez rozróżnienia. */
  status: 'buffer' | 'issued' | null;
  dimension: HmSalesReportDimension;
  grain: HmSalesReportGrain;
  /** Użytkownik zażyczył sobie dociągnięcia szczegółów dokumentów. */
  details: boolean;
  /** Zawężenie do wskazanych dokumentów („wykonaj dla zaznaczonych"); pusta lista = cały zakres. */
  ids: number[];
}

/**
 * Odpowiedź trasy raportu. `buckets` jest tu ZAWSZE puste: koszyki buduje przeglądarka, bo i tak
 * musi je przeliczyć po dociągnięciu szczegółów, a zmiana wymiaru na gotowym raporcie nie może
 * kosztować ponownego przejścia po całym zakresie Symfonii. Trasa dostarcza wiersze i sumę
 * całkowitą — liczoną tą samą funkcją, więc bez ryzyka rozjazdu.
 */
export interface HmSalesReportResponse extends HmSalesReportAggregate {
  dimension: HmSalesReportDimension;
  grain: HmSalesReportGrain;
  /** `true` = szczegóły dokumentów są już w wierszach (rejestr pieniężny, pozycje). */
  detailsLoaded: boolean;
  /** `true` = zakres przekroczył próg i szczegółów świadomie NIE ciągnęliśmy. */
  detailsSkipped: boolean;
  /** Dokumenty, których szczegółu Symfonia nie oddała — wiersze zostają, bez pól szczegółowych. */
  detailsFailed: number[];
  /** `true` = lista z Symfonii uderzyła w nasz limit wierszy; raport jest niepełny. */
  truncated: boolean;
  fetchedAt: string;
  integrationName: string;
  /** Echo parametrów — nagłówek wydruku i CSV biorą je stąd, nie z adresu. */
  params: HmSalesReportParams;
}

/**
 * Szczegół dokumentu — to, czego nie ma na liście Symfonii. Kształt jest wspólny, bo API go
 * produkuje, a przeglądarka wkleja w wiersze raportu.
 */
export interface HmSalesReportDetail {
  id: number;
  paymentRegistryId: number | null;
  paymentRegistryName: string | null;
  buyerIsAdHoc: boolean;
  buyerName: string | null;
  positions: {
    no: number;
    productCode: string;
    productName: string;
    vatRateCode: string | null;
    quantity: string;
    netValue: string;
    vatPln: string;
    grossValue: string;
  }[];
}

/** Wpisuje szczegóły w wiersze nagłówkowe (rejestr pieniężny, nabywca jednokrotny). */
export function mergeDetailsIntoRows(
  rows: HmSalesReportRow[],
  details: HmSalesReportDetail[],
): HmSalesReportRow[] {
  const byId = new Map(details.map((d) => [d.id, d]));
  return rows.map((row) => {
    const detail = byId.get(row.documentId);
    if (!detail) return row;
    return {
      ...row,
      paymentRegistryId: detail.paymentRegistryId,
      paymentRegistryName: detail.paymentRegistryName,
      buyerIsAdHoc: detail.buyerIsAdHoc,
      buyerName: row.buyerName ?? detail.buyerName,
    };
  });
}

/**
 * Rozbija wiersze nagłówkowe na wiersze pozycji. Dokument bez pozycji (szczegółu nie udało się
 * pobrać) ZNIKA z raportu pozycyjnego — pokazanie go z kwotami nagłówka zdublowałoby sumę,
 * a pokazanie z zerami kłamałoby; brakujące dokumenty raport wymienia jako `detailsFailed`.
 */
export function explodeToPositions(
  rows: HmSalesReportRow[],
  details: HmSalesReportDetail[],
): HmSalesReportRow[] {
  const byId = new Map(details.map((d) => [d.id, d]));
  return rows.flatMap((row) => {
    const detail = byId.get(row.documentId);
    if (!detail || detail.positions.length === 0) return [];
    return detail.positions.map((p) => ({
      ...row,
      positionNo: p.no,
      productCode: p.productCode,
      productName: p.productName,
      vatRateCode: p.vatRateCode,
      quantity: p.quantity,
      netValue: p.netValue,
      vatPln: p.vatPln,
      grossValue: p.grossValue,
    }));
  });
}

/* ---------------------------------------------------------------- arytmetyka */

export function sumMoneyByCurrency(rows: HmSalesReportRow[]): HmReportMoney[] {
  const acc = new Map<string, { net: number; vatPln: number; gross: number }>();
  for (const row of rows) {
    const currency = row.currency || 'PLN';
    const entry = acc.get(currency) ?? { net: 0, vatPln: 0, gross: 0 };
    entry.net += toMinor(row.netValue);
    entry.vatPln += toMinor(row.vatPln);
    entry.gross += toMinor(row.grossValue);
    acc.set(currency, entry);
  }
  return Array.from(acc.entries())
    .sort(([a], [b]) => compareCurrency(a, b))
    .map(([currency, e]) => ({
      currency,
      net: fromMinor(e.net),
      vatPln: fromMinor(e.vatPln),
      gross: fromMinor(e.gross),
    }));
}

function moneyDiffers(a: HmReportMoney[], b: HmReportMoney[]): boolean {
  if (a.length !== b.length) return true;
  const index = new Map(b.map((m) => [m.currency, m]));
  return a.some((m) => {
    const other = index.get(m.currency);
    if (!other) return true;
    return (
      toMinor(m.net) !== toMinor(other.net) ||
      toMinor(m.vatPln) !== toMinor(other.vatPln) ||
      toMinor(m.gross) !== toMinor(other.gross)
    );
  });
}

/* ------------------------------------------------------------------ wymiary */

/** Wartość wymiaru dla wiersza: klucz koszyka + jego etykieta. */
export function bucketIdentityOf(
  row: HmSalesReportRow,
  dimension: HmSalesReportDimension,
): HmReportBucketIdentity {
  switch (dimension) {
    case 'buyer':
      return bucketById(row.buyerId, row.buyerName, 'c');
    case 'recipient':
      return bucketById(row.recipientId, row.recipientName, 'r');
    case 'typeCode':
      return bucketByText(row.typeCode, row.typeName ?? row.typeCode);
    case 'character':
      return bucketByText(row.character);
    case 'series':
      return bucketByText(row.series);
    case 'department':
      return bucketById(row.departmentId, row.departmentName, 'd');
    case 'description':
      return bucketByText(row.description);
    case 'currency':
      return bucketByText(row.currency || 'PLN');
    case 'status':
      return row.inBuffer
        ? { key: 'buffer', label: null, labelKey: 'buffer' }
        : { key: 'issued', label: null, labelKey: 'issued' };
    case 'issueDate':
      return bucketByText(row.issueDate?.slice(0, 10) ?? '');
    case 'issueMonth':
      return bucketByText(row.issueDate?.slice(0, 7) ?? '');
    case 'paymentRegistry':
      return bucketById(row.paymentRegistryId, row.paymentRegistryName, 'p');
    case 'product':
      return bucketByText(row.productCode, row.productName || row.productCode);
    case 'vatRate':
      return bucketByText(row.vatRateCode);
    case 'none':
      return HM_REPORT_NONE;
  }
}

/** Wymiary datowe sortujemy chronologicznie, resztę po etykiecie. */
const DATE_DIMENSIONS: readonly HmSalesReportDimension[] = ['issueDate', 'issueMonth'];

/* ---------------------------------------------------------------- agregacja */

export function aggregateSalesReport(input: {
  rows: HmSalesReportRow[];
  dimension: HmSalesReportDimension;
  grain: HmSalesReportGrain;
  /** Wiersze o ziarnie dokumentu — wyłącznie do uzgodnienia sum w trybie pozycji. */
  documentRows?: HmSalesReportRow[];
}): HmSalesReportAggregate {
  const { rows, dimension, grain } = input;

  const documentCount = new Set(rows.map((r) => r.documentId)).size;
  const positionCount = grain === 'position' ? rows.length : null;
  const grandTotals = sumMoneyByCurrency(rows);

  // W trybie pozycji sumy liczymy WYŁĄCZNIE z pozycji — nagłówki nie wchodzą do sumy ani razu,
  // inaczej kwoty dublowałyby się tyle razy, ile pozycji ma dokument.
  const documentTotals =
    grain === 'position' && input.documentRows ? sumMoneyByCurrency(input.documentRows) : null;
  const totalsMismatch = documentTotals != null && moneyDiffers(grandTotals, documentTotals);

  if (dimension === 'none') {
    return {
      rows,
      buckets: null,
      grandTotals,
      documentTotals,
      totalsMismatch,
      documentCount,
      positionCount,
    };
  }

  const acc = new Map<string, { identity: HmReportBucketIdentity; rows: HmSalesReportRow[] }>();
  for (const row of rows) {
    const identity = bucketIdentityOf(row, dimension);
    const entry = acc.get(identity.key) ?? { identity, rows: [] };
    entry.rows.push(row);
    acc.set(identity.key, entry);
  }

  const isDate = DATE_DIMENSIONS.includes(dimension);
  const buckets: HmSalesReportBucket[] = Array.from(acc.values())
    .map(({ identity, rows: bucketRows }) => ({
      key: identity.key,
      label: identity.label,
      labelKey: identity.labelKey,
      count: bucketRows.length,
      documentCount: new Set(bucketRows.map((r) => r.documentId)).size,
      totals: sumMoneyByCurrency(bucketRows),
      rows: bucketRows,
    }))
    .sort((a, b) => compareBuckets(a, b, isDate));

  return {
    rows: [],
    buckets,
    grandTotals,
    documentTotals,
    totalsMismatch,
    documentCount,
    positionCount,
  };
}
